import { createEffect, onCleanup, untrack } from 'solid-js';
import * as d3 from 'd3';
import { defaultChannelsStore } from '../../../../store/defaultChannelsStore';
import { tagsExploreMapMarkers } from '../../../../store/tagsExploreMapMarkersStore';
import { getInterpolatedPointAtTimeFromSortedTrack } from '../../../../utils/trackInterpolation';
import type { TagExploreMapMarker } from '../../../../utils/tagsExploreMapMarkers';

const OVERLAY_CLASS = 'tags-explore-map-markers-overlay';

/** Minimal Mapbox map surface used for projection and canvas container. */
export interface TagsExploreMarkersMap {
  project: (lngLat: [number, number]) => { x: number; y: number };
  getCanvasContainer: () => HTMLElement;
  getContainer: () => HTMLElement;
  on: (ev: string, fn: () => void) => void;
  off: (ev: string, fn: () => void) => void;
}

export interface TagsExploreMarkersLayerProps {
  map: TagsExploreMarkersMap | null | undefined;
  trackData: unknown[];
  enabled: boolean;
}

function getTimestampFromPoint(d: unknown): Date {
  if (d == null || typeof d !== 'object') {return new Date(0);}
  const row = d as Record<string, unknown>;
  const timestamp = row.Datetime;
  if (timestamp == null) {return new Date(0);}
  if (timestamp instanceof Date) {return timestamp;}
  if (typeof timestamp === 'number') {return new Date(timestamp);}
  if (typeof timestamp === 'string') {
    const parsed = new Date(timestamp);
    if (!Number.isNaN(parsed.getTime())) {return parsed;}
  }
  return new Date(0);
}

function makeLatLngAccessors() {
  const { latName, lngName } = defaultChannelsStore;
  const getLat = (d: unknown): number | undefined => {
    if (d == null || typeof d !== 'object') {return undefined;}
    const row = d as Record<string, unknown>;
    const latField = latName();
    const val =
      row[latField] ??
      row[latField.toLowerCase()] ??
      row[latField.toUpperCase()] ??
      row.Lat ??
      row.lat;
    if (val === undefined || val === null) {return undefined;}
    const numVal = Number(val);
    return Number.isNaN(numVal) ? undefined : numVal;
  };
  const getLng = (d: unknown): number | undefined => {
    if (d == null || typeof d !== 'object') {return undefined;}
    const row = d as Record<string, unknown>;
    const lngField = lngName();
    const val =
      row[lngField] ??
      row[lngField.toLowerCase()] ??
      row[lngField.toUpperCase()] ??
      row.Lng ??
      row.lng;
    if (val === undefined || val === null) {return undefined;}
    const numVal = Number(val);
    return Number.isNaN(numVal) ? undefined : numVal;
  };
  return {
    getTimestamp: getTimestampFromPoint,
    getLat,
    getLng,
  };
}

function resolveCanvasContainer(map: TagsExploreMarkersMap): HTMLElement | null {
  try {
    const c = map.getCanvasContainer() as HTMLElement;
    if (c) {return c;}
  } catch {
    /* ignore */
  }
  try {
    return map.getContainer() as HTMLElement;
  } catch {
    return null;
  }
}

export default function TagsExploreMarkersLayer(props: TagsExploreMarkersLayerProps) {
  let rafId: number | null = null;

  const scheduleDraw = () => {
    if (rafId != null) {return;}
    rafId = requestAnimationFrame(() => {
      rafId = null;
      draw();
    });
  };

  const draw = () => {
    untrack(() => {
      const map = props.map;
      if (!map) {return;}
      const container = resolveCanvasContainer(map);
      if (!container) {return;}

      d3.select(container).selectAll(`.${OVERLAY_CLASS}`).remove();

      const enabled = props.enabled;
      const markers = tagsExploreMapMarkers();
      const data = props.trackData;
      if (!enabled || !markers.length || !data?.length) {return;}

      const { getTimestamp, getLat, getLng } = makeLatLngAccessors();
      const interpolateOpts = { getTimestamp, getLat, getLng };
      const sortedTrack = [...(data as unknown[])].sort(
        (a, b) => getTimestamp(a).getTime() - getTimestamp(b).getTime()
      );
      type Positioned = { m: TagExploreMapMarker; x: number; y: number };
      const positioned: Positioned[] = [];

      for (const m of markers) {
        const t = new Date(m.atIso);
        if (Number.isNaN(t.getTime())) {continue;}
        const p = getInterpolatedPointAtTimeFromSortedTrack(sortedTrack, t, interpolateOpts);
        if (!p) {continue;}
        const lat = getLat(p);
        const lng = getLng(p);
        if (lat === undefined || lng === undefined || Number.isNaN(lat) || Number.isNaN(lng)) {continue;}
        try {
          const pt = map.project([lng, lat]);
          if (!Number.isFinite(pt.x) || !Number.isFinite(pt.y)) {continue;}
          positioned.push({ m, x: pt.x, y: pt.y });
        } catch {
          /* ignore projection errors */
        }
      }

      const svg = d3
        .select(container)
        .append('svg')
        .attr('class', OVERLAY_CLASS)
        .attr('aria-hidden', 'true');

      const g = svg.append('g').attr('class', 'tags-explore-map-markers');

      const syncCircleTitle = (sel: d3.Selection<SVGCircleElement, Positioned, SVGGElement, unknown>) => {
        sel.selectAll('title').remove();
        sel.each(function (d) {
          d3.select(this).append('title').text(d.m.tooltip);
        });
      };

      const joined = g
        .selectAll<SVGCircleElement, Positioned>('circle')
        .data(positioned, (d) => d.m.id)
        .join(
          (enter) =>
            enter
              .append('circle')
              .attr('class', (d) => `tags-explore-map-marker tags-explore-map-marker--${d.m.category}`)
              .attr('cx', (d) => d.x)
              .attr('cy', (d) => d.y)
              .attr('r', 5),
          (update) => update.attr('cx', (d) => d.x).attr('cy', (d) => d.y),
          (exit) => exit.remove()
        );
      syncCircleTitle(joined);
    });
  };

  createEffect(() => {
    void defaultChannelsStore.latName();
    void defaultChannelsStore.lngName();
    const map = props.map;
    const en = props.enabled;
    void tagsExploreMapMarkers();
    const td = props.trackData;
    void td?.length;

    const cancelPendingDraw = () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const onMove = () => scheduleDraw();

    onCleanup(() => {
      cancelPendingDraw();
      if (map && en) {
        map.off('move', onMove);
        map.off('zoom', onMove);
        map.off('resize', onMove);
      }
      try {
        if (map) {
          const c = resolveCanvasContainer(map);
          if (c) {d3.select(c).selectAll(`.${OVERLAY_CLASS}`).remove();}
        }
      } catch {
        /* ignore */
      }
    });

    if (!map || !en) {
      scheduleDraw();
      return;
    }

    map.on('move', onMove);
    map.on('zoom', onMove);
    map.on('resize', onMove);

    scheduleDraw();
  });

  return null;
}
